import { TFunction } from 'i18next'

import { FlagCountry } from '@forto-dev/phoenix-ui-core'
import {
  HorizontalTimelineType,
  TimelineDataType,
  TimelineDataTypes,
  VerticalTimelineType,
} from '@forto-dev/phoenix-ui-core/ship'

import { DateTranslationKey } from '@/utils/localisation'

import { DateStatus, getDateStatus, Timeline, TimelineMilestoneKey } from './getTimelineData'
import { combineTimelineContainers } from './timeline'
import { isAirShipment, isLCLShipment } from './transportKind'
import { TimelineHouseCargo } from '../types'

const getMilestoneLabel = (
  key: Exclude<TimelineMilestoneKey, 'transhipment'>,
  dateStatus: DateStatus,
  translate: TFunction<'common', undefined>,
  modeOfTransport?: string
): string => {
  if (key === 'departure' && dateStatus === 'actual' && modeOfTransport === 'air') {
    return translate(`timeline.${key}_${dateStatus}_${modeOfTransport}`)
  }
  return translate(`timeline.${key}_${dateStatus}`)
}

// FCL always lists its containers; LCL (air is always LCL) only when a container number is known.
const shouldShowContainerRow = (timelineData: Timeline, houseCargo: TimelineHouseCargo): boolean => {
  const isLCL = isLCLShipment({
    mainTransportMode: timelineData.transportMode,
    mainTransportType: timelineData.transportType,
  })
  return !isLCL || (houseCargo.containerNumber != null && houseCargo.containerNumber !== '')
}

const isAirTimeline = (timelineData: Timeline): boolean =>
  isAirShipment({ mainTransportMode: timelineData.transportMode })

const pushAirCarriageRows = (
  values: TimelineDataTypes[],
  airline: string | undefined,
  flightNumber: string | undefined,
  translate: TFunction<'common', undefined>
) => {
  if (airline) {
    values.push({
      type: TimelineDataType.LabelValue,
      label: `${translate('details.details.general.values.airline')}:`,
      value: airline,
    })
  }
  if (flightNumber) {
    values.push({
      type: TimelineDataType.LabelValue,
      label: `${translate('booking.details.general.values.flightNumber')}:`,
      value: flightNumber,
    })
  }
}

const getTimelineState = (data: 'estimated' | 'confirmed' | 'actual' | 'not-applicable'): TimelineItem['state'] => {
  switch (data) {
    case 'estimated':
      return 'upcoming'
    case 'confirmed':
      return 'active'
    case 'actual':
      return 'completed'
    case 'not-applicable':
      return 'not-applicable'
    default:
      return 'negative'
  }
}

export const getHorizontalTimeline = (
  timelineData: Timeline,
  translate: TFunction<'common', undefined>,
  translateDate: (key: DateTranslationKey, date: Date) => string
): HorizontalTimelineType => {
  const timeline: HorizontalTimelineType = {
    arrival: getTimelineItem(timelineData, 'arrival', translate, translateDate, true),
    delivery: getTimelineItem(timelineData, 'delivery', translate, translateDate, true),
    departure: {
      ...getTimelineItem(timelineData, 'departure', translate, translateDate, true),
      transhipmentData: timelineData.transhipment ?? undefined,
    },
    origin: getTimelineItem(timelineData, 'origin', translate, translateDate, true),
  }
  return timeline
}

const getTransportModeValue = (mode: string, translate: TFunction<'common', undefined>): string => {
  //@ts-expect-error no translations
  return mode ? translate(`shipment.transport_mode.${mode}`) : '-'
}

const getDepartureData = (
  timelineData: Timeline,
  houseCargos: TimelineHouseCargo[] = [],
  translate: TFunction<'common', undefined>,
  translateDate: (key: DateTranslationKey, date: Date) => string
): TimelineDataTypes[][] => {
  const departureData: TimelineDataTypes[][] = houseCargos.map((houseCargo) => {
    const values: TimelineDataTypes[] = []

    if (shouldShowContainerRow(timelineData, houseCargo)) {
      values.push({
        type: TimelineDataType.LabelValue,
        label: translate('shipment.timeline.containers'),
        value: houseCargo.containerNumber || '—',
      })
    }

    if (houseCargo.departureAddress) {
      values.push({
        type: TimelineDataType.LabelValue,
        label: `${translate('shipment.timeline.delivery_address')}:`,
        value: houseCargo.departureAddress,
      })
    }
    const isAir = isAirTimeline(timelineData)
    timelineData.departure.outgoingConnections.forEach((connection) => {
      if (connection.modeOfTransport) {
        values.push({
          type: TimelineDataType.LabelValue,
          label: `${translate('booking.details.general.values.mode')}:`,
          value: getTransportModeValue(connection.modeOfTransport, translate),
        })
      }
      // Air legs name the airline and flight, falling back to the shipment-level facts; there is no vessel.
      if (isAir) {
        pushAirCarriageRows(
          values,
          connection.vendor?.partnerCode || connection.vendor?.accountID || timelineData.airline,
          connection.flightNumber || timelineData.flightNumber,
          translate
        )
        return
      }
      if (connection.vendor) {
        values.push({
          type: TimelineDataType.LabelValue,
          label: `${translate('booking.details.general.values.carrier')}:`,
          value: connection.vendor.partnerCode || connection.vendor.accountID || '—',
        })
      }

      if (connection.vesselName) {
        values.push({
          type: TimelineDataType.LabelValue,
          label: `${translate('booking.details.general.values.vessel')}:`,
          value: connection.vesselName,
        })
      }

      if (connection.flightNumber) {
        values.push({
          type: TimelineDataType.LabelValue,
          label: `${translate('booking.details.general.values.flightNumber')}:`,
          value: connection.flightNumber,
        })
      }
    })
    if (isAir && timelineData.departure.outgoingConnections.length === 0) {
      pushAirCarriageRows(values, timelineData.airline, timelineData.flightNumber, translate)
    }

    if (houseCargo.milestones?.cargoLoadedAtPort?.date) {
      values.push({
        type: TimelineDataType.LabelValue,
        label: houseCargo.milestones?.cargoLoadedAtPort?.isActual ? `Loaded at POL:` : `Est. Load at POL:`,
        value: translateDate('shipment_timeline_date', houseCargo.milestones?.cargoLoadedAtPort?.date) || '—',
      })
    }
    return values
  })
  return combineTimelineContainers(departureData, translate)
}

const getOriginData = (
  timelineData: Timeline,
  houseCargos: TimelineHouseCargo[] = [],
  translate: TFunction<'common', undefined>,
  translateDate: (key: DateTranslationKey, date: Date) => string
): TimelineDataTypes[][] => {
  const originData: TimelineDataTypes[][] = houseCargos.map((houseCargo) => {
    const values: TimelineDataTypes[] = []
    if (shouldShowContainerRow(timelineData, houseCargo)) {
      values.push({
        type: TimelineDataType.LabelValue,
        label: translate('shipment.timeline.containers'),
        value: houseCargo.containerNumber || '—',
      })
    }

    if (houseCargo.originAddress) {
      values.push({
        type: TimelineDataType.LabelValue,
        label: `${translate('shipment.timeline.delivery_address')}:`,
        value: houseCargo.originAddress,
      })
    }
    if (houseCargo?.milestones?.cargoReadyDate?.date) {
      values.push({
        type: TimelineDataType.LabelValue,
        label: houseCargo?.milestones?.cargoReadyDate?.isActual
          ? translate('shipment.timeline.cargo_ready')
          : translate('shipment.timeline.cargo_ready_est'),
        value: translateDate('shipment_timeline_date', houseCargo?.milestones?.cargoReadyDate?.date),
      })
    }
    // Per-container pickup when the platform provides it, otherwise the place-of-pickup node value,
    // which is the only source for shipments without per-container milestones (e.g. air, LCL).
    const cargoPickUp = houseCargo?.milestones?.pickUpDate
    const pickUp = cargoPickUp?.date
      ? cargoPickUp
      : { date: timelineData.origin.pickupDate, isActual: timelineData.origin.pickupIsActual }

    if (pickUp.date) {
      values.push({
        type: TimelineDataType.LabelValue,
        label: pickUp.isActual ? translate('shipment.timeline.pick_up') : translate('shipment.timeline.pick_up_est'),
        value: translateDate('shipment_timeline_date', pickUp.date),
      })
    }
    if (houseCargo?.milestones?.cargoGateIn?.date) {
      values.push({
        type: TimelineDataType.LabelValue,
        label: houseCargo?.milestones?.cargoGateIn?.isActual
          ? translate('shipment.timeline.gate_in')
          : translate('shipment.timeline.gate_in_est'),
        value: translateDate('shipment_timeline_date', houseCargo?.milestones?.cargoGateIn?.date),
      })
    }
    return values
  })
  return combineTimelineContainers(originData, translate)
}

const getArrivalData = (
  timelineData: Timeline,
  houseCargos: TimelineHouseCargo[] = [],
  translate: TFunction<'common', undefined>,
  translateDate: (key: DateTranslationKey, date: Date) => string
): TimelineDataTypes[][] => {
  const arrivalData: TimelineDataTypes[][] = houseCargos.map((houseCargo) => {
    const values: TimelineDataTypes[] = []
    if (shouldShowContainerRow(timelineData, houseCargo)) {
      values.push({
        type: TimelineDataType.LabelValue,
        label: translate('shipment.timeline.containers'),
        value: houseCargo.containerNumber || '—',
      })
    }

    if (houseCargo.arrivalAddress) {
      values.push({
        type: TimelineDataType.LabelValue,
        label: `${translate('shipment.timeline.delivery_address')}:`,
        value: houseCargo.arrivalAddress,
      })
    }
    if (houseCargo.milestones?.cargoUnloadedAtPort?.date) {
      values.push({
        type: TimelineDataType.LabelValue,
        label: houseCargo.milestones?.cargoUnloadedAtPort?.isActual
          ? translate('shipment.timeline.discharge_POD')
          : translate('shipment.timeline.discharge_POD_est'),
        value: translateDate('shipment_timeline_date', houseCargo.milestones?.cargoUnloadedAtPort?.date),
      })
    }
    if (houseCargo.milestones?.cargoGateOut?.date) {
      values.push({
        type: TimelineDataType.LabelValue,
        label: houseCargo.milestones?.cargoGateOut?.isActual
          ? translate('shipment.timeline.gate_out')
          : translate('shipment.timeline.gate_out_est'),
        value: translateDate('shipment_timeline_date', houseCargo.milestones?.cargoGateOut?.date),
      })
    }
    return values
  })

  return combineTimelineContainers(arrivalData, translate)
}

const getDeliveryData = (
  timelineData: Timeline,
  houseCargos: TimelineHouseCargo[] = [],
  translate: TFunction<'common', undefined>,
  translateDate: (key: DateTranslationKey, date: Date) => string
): TimelineDataTypes[][] | undefined => {
  let items = houseCargos.map((houseCargo) => {
    const values: TimelineDataTypes[] = []

    if (shouldShowContainerRow(timelineData, houseCargo)) {
      values.push({
        type: TimelineDataType.LabelValue,
        label: translate('shipment.timeline.containers'),
        value: houseCargo.containerNumber || '—',
      })
    }

    if (houseCargo.deliveryAddress) {
      values.push({
        type: TimelineDataType.LabelValue,
        label: `${translate('shipment.timeline.delivery_address')}:`,
        value: houseCargo.deliveryAddress,
      })
    }

    if (houseCargo?.milestones?.timeOfDelivery?.date) {
      values.push({
        type: TimelineDataType.LabelValue,
        label: `${translate(`timeline.delivery_${getDateStatus(houseCargo?.milestones?.timeOfDelivery.isActual)}`)}:`,
        value: translateDate('shipment_timeline_date', houseCargo?.milestones?.timeOfDelivery?.date),
      })
    }
    if (houseCargo?.milestones?.emptyContainerDropOff?.date) {
      values.push({
        type: TimelineDataType.LabelValue,
        label: `${translate(`shipment.timeline.empty_container_return_${getDateStatus(houseCargo?.milestones?.emptyContainerDropOff.isActual)}`)}:`,
        value: translateDate('shipment_timeline_date', houseCargo?.milestones?.emptyContainerDropOff?.date),
      })
    }

    if (
      !houseCargo?.milestones?.timeOfDelivery?.date &&
      !houseCargo?.milestones?.emptyContainerPickup?.date &&
      !timelineData.delivery.location
    ) {
      return null
    }
    return values
  })

  items = items.filter((item) => item !== null)
  return items.length ? combineTimelineContainers(items as TimelineDataTypes[][], translate) : undefined
}

export const getVerticalTimeline = (
  timelineData: Timeline,
  translate: TFunction<'common', undefined>,
  translateDate: (key: DateTranslationKey, date: Date) => string,
  houseCargos?: TimelineHouseCargo[]
): VerticalTimelineType => {
  const timeline: VerticalTimelineType = {
    arrival: {
      ...getTimelineItem(timelineData, 'arrival', translate, translateDate),
      arrivalData: getArrivalData(timelineData, houseCargos, translate, translateDate),
    },
    delivery: {
      ...getTimelineItem(timelineData, 'delivery', translate, translateDate),
      deliveryData: getDeliveryData(timelineData, houseCargos, translate, translateDate),
    },
    departure: {
      ...getTimelineItem(timelineData, 'departure', translate, translateDate),
      departureData: getDepartureData(timelineData, houseCargos, translate, translateDate),
    },
    origin: {
      ...getTimelineItem(timelineData, 'origin', translate, translateDate),
      originData: getOriginData(timelineData, houseCargos, translate, translateDate),
    },
    transhipment: getTranshipmentData(timelineData, translate, translateDate),
  }

  return timeline
}

type TimelineItem = {
  title: string
  titleTooltip?: string
  label: string
  labelValue: string
  flag?: FlagCountry
  state: 'active' | 'completed' | 'upcoming' | 'negative' | 'not-applicable'
  progress: number
  delayText?: string
  bar?: 'dashed' | 'solid'
}

const getDelayText = (
  delayDays: number | undefined,
  translate: TFunction<'common', undefined>,
  isHorizontal?: boolean,
  step?: Exclude<TimelineMilestoneKey, 'transhipment'>
) => {
  if (!delayDays) {
    return undefined
  }
  if (!isHorizontal) {
    switch (step) {
      case 'arrival':
        return translate('overview.state.delayedArrival', { delayDays })
      case 'departure':
        return translate('overview.state.delayedDeparture', { delayDays })
      case 'origin':
        return translate('overview.state.delayedDeparture', { delayDays })
      case 'delivery':
        return translate('overview.state.delayedDelivery', { delayDays })
      default:
        return translate('overview.state.delayedArrival', { delayDays })
    }
  }
  return `+${delayDays}D`
}

export const getTimelineItem = (
  timelineData: Timeline,
  step: Exclude<TimelineMilestoneKey, 'transhipment'>,
  translate: TFunction<'common', undefined>,
  translateDate: (key: DateTranslationKey, date: Date) => string,
  isHorizontal?: boolean
): TimelineItem => {
  const timelineItem = timelineData[step]
  const label = getMilestoneLabel(step, timelineItem.dateStatus, translate, timelineData.transportMode)

  return {
    label: timelineItem.date ? label : '',
    labelValue: (timelineItem.date && translateDate('shipment_timeline_date', timelineItem.date)) || '',
    state: getTimelineState(timelineItem.isHandlingByThirdParty ? 'not-applicable' : timelineItem.dateStatus),
    delayText: getDelayText(timelineItem.delayDays, translate, isHorizontal, step),
    titleTooltip: timelineItem.isHandlingByThirdParty
      ? translate('shipment.timeline.service_not_handled_by_forto')
      : `${timelineItem.location} ${
          timelineItem.date ? label : ''
        } ${(timelineItem.date && translateDate('shipment_timeline_date', timelineItem.date)) || ''}`,
    title: timelineItem.location || '—',
    progress: timelineItem.progress,
    flag: timelineItem.flag,
    bar: timelineItem.isHandlingByThirdParty ? 'dashed' : undefined,
  }
}

const getTranshipmentData = (
  timelineData: Timeline,
  translate: TFunction<'common', undefined>,
  translateDate: (key: DateTranslationKey, date: Date) => string
) => {
  return timelineData.transhipment
    ? {
        total: timelineData.transhipment.total,
        completed: timelineData.transhipment.completed,
        transhipmentData: timelineData.transhipment.data.map((item) => {
          const values: TimelineDataTypes[] = []

          if (item.port) {
            const isAirLeg = (item.modeOfTransport ?? timelineData.transportMode) === 'air'
            values.push({
              type: TimelineDataType.LabelValue,
              label: `${translate(isAirLeg ? 'timeline.airport' : 'timeline.port')}:`,
              value: item.port,
            })
          }

          const date = item.departure || item.arrival

          if (date) {
            const labelKey: 'departure' | 'arrival' = item.departure ? 'departure' : 'arrival'
            const dateLabel = getMilestoneLabel(
              labelKey,
              item.dateStatus,
              translate,
              item.modeOfTransport ?? timelineData.transportMode
            )

            values.push({
              type: TimelineDataType.LabelValue,
              label: `${dateLabel}:`,
              value: translateDate('shipment_timeline_date', date),
            })
          }

          if (item.vesselName) {
            values.push({
              type: TimelineDataType.LabelValue,
              label: `${translate('timeline.vessel')}:`,
              value: item.vesselName,
            })
          }

          if (item.flightNumber) {
            values.push({
              type: TimelineDataType.LabelValue,
              label: `${translate('booking.details.general.values.flightNumber')}:`,
              value: item.flightNumber,
            })
          }

          return values
        }),
      }
    : undefined
}
